import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import { useGetMyLeavesQuery, appApi } from "../../../../store/appApi";
import {
  updateLeaveStatusApi,
  deleteLeaveApi,
} from "../../../../services/leave";
import { selectUserRole } from "../../../../store/authSlice";
import "../style.css";
const STATUS_BADGE = {
  pending:  "warning",
  approved: "success",
  rejected: "danger",
};

const STATUS_LABEL = {
  pending:  "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const LeaveList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [statusFilter, setStatusFilter] = useState("");
  const { data, isLoading, isFetching } = useGetMyLeavesQuery(statusFilter);
  const leaves = data?.leaves || [];

  const handleStatus = async (id, newStatus) => {
    const confirmed = await Swal.fire({
      title: `${newStatus === "approved" ? "Approve" : "Reject"} this leave?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newStatus === "approved" ? "#2bc155" : "#f32d2d",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await updateLeaveStatusApi(id, newStatus);
      dispatch(appApi.util.invalidateTags(["Leaves"]));
    } catch {
      Swal.fire("Error", "Action failed.", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await Swal.fire({
      title: "Cancel this leave application?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f32d2d",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await deleteLeaveApi(id);
      dispatch(appApi.util.invalidateTags(["Leaves"]));
    } catch {
      Swal.fire("Error", "Could not cancel.", "error");
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={isSuperAdmin ? "All Leave Applications" : "My Leaves"}
        motherMenu="Leave"
      />
      <div className="card">
        <div className="card-header d-flex align-items-center gap-2 flex-wrap">
          <h4 className="card-title mb-0 me-auto">
            {isSuperAdmin ? "All Leave Applications" : "My Leave Applications"}
          </h4>
          <select
            className="form-control"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {!isSuperAdmin && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate("/staff/leave/apply")}
            >
              + Apply
            </button>
          )}
        </div>

        <div className="card-body">
          {isLoading || isFetching ? (
            <div className="text-center py-4">Loading…</div>
          ) : leaves.length === 0 ? (
            <div className="text-center text-muted py-4">
              No leave applications found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    {isSuperAdmin && <th>Employee</th>}
                    {isSuperAdmin && <th>Project</th>}
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Status</th>
                    <th>Applied On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((l) => (
                    <tr key={l._id}>
                      {isSuperAdmin && (
                        <td>
                          <div className="fw-semibold">{l.username || "—"}</div>
                          <small className="text-muted">{l.userrole}</small>
                        </td>
                      )}
                      {isSuperAdmin && (
                        <td>{l.projectName || "—"}</td>
                      )}
                      <td>
                        {l.leaveType}
                        {l.isShortLeave && (
                          <span
                            className="badge d-block mt-1"
                            style={{ background: "#8b5cf6", fontSize: 10 }}
                          >
                            ⏱ Short {l.shortLeaveFrom}-{l.shortLeaveTo}
                          </span>
                        )}
                        {l.createdByAdmin && (
                          <span className="badge bg-info d-block mt-1" style={{ fontSize: 10 }}>
                            Marked by admin
                          </span>
                        )}
                      </td>
                      <td>
                        {l.leaveFrom
                          ? new Date(l.leaveFrom).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        {l.leaveTo
                          ? new Date(l.leaveTo).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>{l.totalLeaves}</td>
                      <td>
                        <div className="d-flex flex-column gap-1" style={{ minWidth: 110 }}>
                          <span className={`badge bg-${STATUS_BADGE[l.status] || "secondary"}`}>
                            {STATUS_LABEL[l.status] || l.status}
                          </span>
                          {l.attendanceStatus && (
                            <span
                              className={`badge bg-${l.attendanceStatus === "half-day" ? "warning" : "success"}`}
                              style={{ fontSize: 10 }}
                            >
                              {l.attendanceStatus === "half-day" ? "⚑ Half Day Present" : "✓ Was Present"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {l.created
                          ? new Date(l.created).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <div
                          className="d-flex flex-column gap-1"
                          style={{ width: 96 }}
                        >
                          <button
                            className="btn btn-xs btn-outline-primary w-100"
                            onClick={() => navigate(`/staff/leave/${l._id}`)}
                          >
                            View
                          </button>

                          {/* Super-admin approve / reject on pending */}
                          {isSuperAdmin && l.status === "pending" && (
                            <>
                              <button
                                className="btn btn-xs btn-success w-100"
                                onClick={() => handleStatus(l._id, "approved")}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-xs btn-danger w-100"
                                onClick={() => handleStatus(l._id, "rejected")}
                              >
                                Reject
                              </button>
                            </>
                          )}

                          {/* Employee can cancel their own pending application */}
                          {!isSuperAdmin && l.status === "pending" && (
                            <button
                              className="btn btn-xs btn-outline-danger w-100"
                              onClick={() => handleDelete(l._id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeaveList;
